import React from 'react'
import { compose } from 'recompose'
import { connect, ConnectedProps } from 'react-redux'
import {
  BForm,
  BFormGroup,
  BFormText,
  BLabel,
  BTooltipMark,
  BUncontrolledTooltip,
} from '@prague-digi/ui-components'
import get from 'lodash/get'

import { ReduxState } from '../../types'
import { clientActions, modalDialogActions } from '../../redux'
import * as codetableUtils from '../../utils/codetable'
import messages from '../../i18n'
import {
  errorHandler,
  form,
  FormInterface,
  intl,
  IntlInterface,
  wizard,
  WizardInterface,
} from '../../hoc'
import {
  BCombobox,
  BTextInput,
  PageControlButtons,
  PrefillFormButton,
  RadioButton,
  Title,
} from '../../components/'
import validate from './ClientAdditionalInfo.validate'
import { SecondDocumentType, PepOptions } from '../../constants'
import { API_ERROR } from '../../constants/modals'
import { ApiErrorInfo, Response } from '../../api/api'
import styles from './styles.scss'

interface Props
  extends WizardInterface,
    FormInterface,
    IntlInterface,
    ConnectedProps<typeof connector> {
  isModal?: boolean
  onClose: any
  onContinue: any
}

const FORM_NAME = 'ClientAdditionalInfo'

export class ClientAdditionalInfo extends React.Component<Props> {
  constructor(props: Props) {
    super(props)
    props.initialize({ ...props.storeData })
  }

  onSubmit = () => {
    this.props.handleSubmit(this.saveClientAdditionalInfoCallback)()
  }

  onBack = () => {
    if (this.props.onClose) {
      this.props.onClose()
    } else {
      this.props.wizard.gotoPrevious()
    }
  }

  getOtherDocumentError = () =>
    get(this.props, 'fields.otherDocumentType.touched') &&
    get(this.props, 'errors.otherDocumentType')

  saveClientAdditionalInfoCallback = () => {
    this.props.saveClientAdditionalInfo({
      success: () => {
        if (this.props.onContinue) {
          this.props.onContinue()
        } else {
          this.props.wizard.gotoNext()
        }
      },
      error: (errorInfo: ApiErrorInfo) => {
        if (errorInfo.type === Response.dataError && errorInfo.errors) {
          this.props.setErrors(FORM_NAME, errorInfo.errors)
        } else {
          this.props.showModalDialog(API_ERROR({ errorInfo, onRetry: this.onSubmit }))
        }
      },
    })
  }

  handlePepNo = () => {
    this.props.change('pepReason', '')
  }

  render() {
    const { isModal, education, housingTypes, invalid, stayDurations, translate } = this.props
    const pep = get(this.props, 'values.pep')

    return (
      <BForm id={FORM_NAME}>
        <Title
          center={isModal}
          message={messages.ClientAdditionalInfo.formTitle}
          subTitle={!isModal}
        />

        <BCombobox
          name="education"
          data={education}
          messages={{
            label: translate(messages.ClientAdditionalInfo.education),
          }}
          testid="education"
        />
        <BCombobox
          name="stayOnAddressDuration"
          data={stayDurations}
          messages={{
            label: translate(messages.ClientAdditionalInfo.stayOnAddressFrom),
          }}
          testid="stayOnAddressDuration"
        />
        <BCombobox
          name="livingType"
          data={housingTypes}
          messages={{
            label: translate(messages.ClientAdditionalInfo.livingType),
          }}
          testid="livingType"
        />
        <BFormGroup>
          <BLabel>
            {translate(messages.ClientAdditionalInfo.pep.title)}
            <BTooltipMark id="clientAdditionalInfoPepTooltip" />
            <BUncontrolledTooltip
              className={styles.tooltip}
              text={translate(messages.ClientAdditionalInfo.pep.tooltip)}
              target="clientAdditionalInfoPepTooltip"
            />
          </BLabel>
          <RadioButton
            message={translate(messages.common.yes)}
            name="pep"
            option={PepOptions.Yes}
            testid="pepYes"
            theme={styles.blockButton}
          />
          <RadioButton
            message={translate(messages.common.no)}
            name="pep"
            onClick={this.handlePepNo}
            option={PepOptions.No}
            testid="pepNo"
            theme={styles.blockButton}
          />
          {pep === PepOptions.Yes && (
            <BTextInput
              name="pepReason"
              messages={{
                label: (
                  <BLabel
                    tooltip={{
                      id: 'clientAdditionalInfoPepReasonTooltip',
                      text: translate(messages.ClientAdditionalInfo.pep.reason.tooltip),
                    }}>
                    {translate(messages.ClientAdditionalInfo.pep.reason.title)}
                  </BLabel>
                ),
              }}
              testid="pepReason"
            />
          )}
        </BFormGroup>
        <BFormGroup error={!!this.getOtherDocumentError()}>
          <BLabel>{translate(messages.ClientAdditionalInfo.extraDocs)}</BLabel>
          <RadioButton
            name="otherDocumentType"
            message={translate(messages.ClientAdditionalInfo.drivingLicense)}
            testid="drivingLicense"
            option={SecondDocumentType.DRIVING_LICENSE}
            theme={styles.blockButton}
          />
          <RadioButton
            name="otherDocumentType"
            message={translate(messages.ClientAdditionalInfo.passport)}
            testid="passport"
            option={SecondDocumentType.PASSPORT}
            theme={styles.blockButton}
          />
          <BFormText>{translate(messages.ClientAdditionalInfo.extraDocsNote)}</BFormText>
          <BFormText>{this.getOtherDocumentError()}</BFormText>
        </BFormGroup>

        <PageControlButtons
          disableSubmit={invalid}
          isModal={isModal}
          onSubmit={this.onSubmit}
          secondaryAction={this.onBack}
        />

        <PrefillFormButton formName="ClientAdditionalInfo" />
      </BForm>
    )
  }
}

const connector = connect(
  (state: ReduxState) => ({
    education: codetableUtils.toOptions(state.codetable.education),
    housingTypes: codetableUtils.toOptions(state.codetable.housingTypes),
    stayDurations: codetableUtils.toOptions(state.codetable.stayDurations),
    storeData: {
      education: state.client.education,
      stayOnAddressDuration: state.client.stayOnAddressDuration,
      pep: state.client.pep,
      pepReason: state.client.pepReason,
      livingType: state.client.livingType,
      otherDocumentType: state.client.otherDocumentType,
    },
  }),
  {
    saveClientAdditionalInfo: clientActions.saveClientAdditionalInfo,
    showModalDialog: modalDialogActions.showModalDialog,
  }
)

export default compose(
  errorHandler('containers/ClientAdditionalInfo/ClientAdditionalInfo.tsx'),
  wizard,
  connector,
  form({
    name: FORM_NAME,
    validate,
  }),
  intl
)(ClientAdditionalInfo)
